import { Admin, CustomRoutes, Resource } from "react-admin";
import { Route } from "react-router-dom";
import { Layout } from "./Layout";
import { authProvider } from "./authProvider";
import { ReviewsList } from "./ReviewsList";
import { ReviewShow } from "./ReviewShow";
import { LoginPage } from "./LoginPage";
import i18nProvider from "./i18nProvider";
import { universalDataProvider } from "./universalDataProvider";
import { AdsModerationList } from "./AdsModerationList";
import { AdsExpertModerationList } from "./AdsModerationList";
import { AdModerationShow } from "./AdModerationShow";
import { AdExpertModerationShow } from "./AdExpertModerationShow";
import { ComplaintsList } from "./ComplaintsList";
import { ComplaintShow } from "./ComplaintShow";
import { OrdersSearchPage } from "./OrdersSearchPage";
import { UsersSearchPage } from "./UsersSearchPage";
import { UserAdsSearchPage } from "./UserAdsSearchPage";
import { OrderShow } from "./OrderShow";
import { UsersShow } from "./UsersShow";
import { UserAdShow } from "./UserAdShow";
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import ReportIcon from '@mui/icons-material/Report';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RuleIcon from '@mui/icons-material/Rule';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BarChartIcon from '@mui/icons-material/BarChart';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { HomePage } from "./HomePage";
import { ReasonsPage } from "./ReasonsPage";
import { AdminUsersPage } from "./AdminUsersPage";
import { ExpertStatisticsPage } from "./ExpertStatisticsPage";
import { ExpertPersonalStatisticsPage } from "./ExpertPersonalStatisticsPage";
import { ExpertTariffsPage } from "./ExpertTariffsPage";
import { ExpertSlaPage } from "./ExpertSlaPage";
import { ModeratorPersonalStatisticsPage, ModeratorsStatisticsPage } from "./ModeratorActionsStatisticsPage";
import { SellerDeficitsPage } from "./SellerDeficitsPage";
import { AppRole, canAccessExpertModerationDetails, canAccessPage } from "./accessControl";
import { ProfileSettingsPage } from "./ProfileSettingsPage";
import { PromoCodesPage } from "./PromoCodesPage";
import { AiModerationSettingsPage, ProjectSettingsPage } from "./ProjectSettingsPage";
import { RemoteConfigPage } from "./RemoteConfigPage";

export const App = () => (
  <Admin
    layout={Layout}
    authProvider={authProvider}
    dataProvider={universalDataProvider}
    loginPage={LoginPage}
    i18nProvider={i18nProvider}
    dashboard={HomePage}
    requireAuth
  >
    {(permissions) => (
      (() => {
        const role = (permissions || null) as AppRole | null;

        return (
          <>
            <CustomRoutes>
              <Route path="/profile-settings" element={<ProfileSettingsPage />} />
              {canAccessPage(role, 'projectSettings') && (
                <Route path="/project-settings" element={<ProjectSettingsPage />} />
              )}
              {canAccessPage(role, 'aiModerationSettings') && (
                <Route path="/project-settings/ai-moderation" element={<AiModerationSettingsPage />} />
              )}
              {canAccessPage(role, 'remoteConfig') && (
                <Route path="/remote-config" element={<RemoteConfigPage />} />
              )}
            </CustomRoutes>
            {canAccessPage(role, 'reviews') && (
              <Resource
                name="reviews"
                list={ReviewsList}
                show={ReviewShow}
                options={{ label: 'Модерация: отзывы' }}
                icon={GavelIcon}
              />
            )}
            {canAccessPage(role, 'adsModeration') && (
              <Resource
                name="adsModeration"
                list={AdsModerationList}
                show={AdModerationShow}
                options={{ label: 'Модерация: объявления' }}
                icon={GavelIcon}
              />
            )}
            {canAccessPage(role, 'complaints') && (
              <Resource
                name="complaints"
                list={ComplaintsList}
                show={ComplaintShow}
                options={{ label: 'Модерация: жалобы пользователей' }}
                icon={ReportIcon}
              />
            )}
            {canAccessPage(role, 'adsExpertModeration') && (
              <Resource
                name="adsExpertModeration"
                list={AdsExpertModerationList}
                show={canAccessExpertModerationDetails(role) ? AdExpertModerationShow : undefined}
                options={{ label: 'Экспертная модерация' }}
                icon={SearchIcon}
              />
            )}
            {canAccessPage(role, 'orders') && (
              <Resource
                name="orders"
                list={OrdersSearchPage}
                show={OrderShow}
                options={{ label: 'Администрирование: управление заказами' }}
                icon={ShoppingCartIcon}
              />
            )}
            {canAccessPage(role, 'users') && (
              <Resource
                name="users"
                list={UsersSearchPage}
                show={UsersShow}
                options={{ label: 'Поиск: пользователи' }}
                icon={PersonSearchIcon}
              />
            )}
            {canAccessPage(role, 'userAdInfo') && (
              <Resource
                name="userAds"
                list={UserAdsSearchPage}
                show={UserAdShow}
                options={{ label: 'Поиск: объявления' }}
              />
            )}
            {canAccessPage(role, 'customReasons') && (
              <Resource
                name="customReasons"
                list={ReasonsPage}
                options={{ label: 'Администрирование: причины отказа' }}
                icon={RuleIcon}
              />
            )}
            {canAccessPage(role, 'promoCodes') && (
              <Resource
                name="promoCodes"
                list={PromoCodesPage}
                options={{ label: 'Настройки: промокоды' }}
                icon={LocalOfferIcon}
              />
            )}
            {canAccessPage(role, 'expertStatistics') && (
              <Resource
                name="expertStatistics"
                list={ExpertStatisticsPage}
                options={{ label: 'Статистика экспертов' }}
                icon={BarChartIcon}
              />
            )}
            {canAccessPage(role, 'expertPersonalStatistics') && (
              <Resource
                name="expertPersonalStatistics"
                list={ExpertPersonalStatisticsPage}
                options={{ label: 'Моя статистика эксперта' }}
                icon={BarChartIcon}
              />
            )}
            {canAccessPage(role, 'moderatorPersonalStatistics') && (
              <Resource
                name="moderatorPersonalStatistics"
                list={ModeratorPersonalStatisticsPage}
                options={{ label: 'Моя статистика модератора' }}
                icon={BarChartIcon}
              />
            )}
            {canAccessPage(role, 'moderatorStatistics') && (
              <Resource
                name="moderatorStatistics"
                list={ModeratorsStatisticsPage}
                options={{ label: 'Статистика команды' }}
                icon={BarChartIcon}
              />
            )}
            {canAccessPage(role, 'sellerDeficits') && (
              <Resource
                name="sellerDeficits"
                list={SellerDeficitsPage}
                options={{ label: 'Дефициты' }}
                icon={BarChartIcon}
              />
            )}
            {canAccessPage(role, 'adminUsers') && (
              <Resource
                name="adminUsers"
                list={AdminUsersPage}
                options={{ label: 'Управление пользователями' }}
                icon={ManageAccountsIcon}
              />
            )}
            {canAccessPage(role, 'expertTariffs') && (
              <Resource
                name="expertTariffs"
                list={ExpertTariffsPage}
                options={{ label: 'Тарифы для экспертов' }}
                icon={CurrencyRubleIcon}
              />
            )}
            {canAccessPage(role, 'expertSla') && (
              <Resource
                name="expertSla"
                list={ExpertSlaPage}
                options={{ label: 'SLA экспертов' }}
                icon={AccessTimeIcon}
              />
            )}
          </>
        );
      })()
    )}
  </Admin>
);
